import validateColor from 'validate-color';
import { App } from 'click-types';
import { get, pick } from 'lodash';
import { ComponentType, ElementType } from '@common/types/element';

const dynamicColorsArr = [
  '@primaryColor',
  '@secondaryColor',
  '@backgroundColor',
  '@textColor',
];
export type AppInfoColor = Pick<
  App,
  'primaryColor' | 'secondaryColor' | 'backgroundColor' | 'textColor'
>;
export const getDynamicColor = (
  colorStr: string,
  appColors?: AppInfoColor | null
) => {
  if (!appColors) {
    return colorStr;
  }
  for (const dynamicColor of dynamicColorsArr) {
    // ignore `@` character
    const appDynamicColorName = dynamicColor.substring(1);
    const appDynamicColorValue = get(appColors, appDynamicColorName);
    if (colorStr === dynamicColor && validateColor(appDynamicColorValue)) {
      return appDynamicColorValue;
    }
  }
  return colorStr;
};

const COLOR_KEYS = [
  'color',
  'backgroundColor',
  'secondaryBackgroundColor',
  'borderColor',
  'buttonColor',
  'descriptionColor',
  'eventBgColor',
  'activeColor',
  'inactiveColor',
  'placeholderColor',
  'errorColor',
  'accentColor',
  'bgGradientFirst',
  'bgGradientSecond',
  'textColor',
  'disabledTextColor',
  'iconColor',
  'iconBackgroundColor',
  'selectedColor',
  'selectedFontColor',
  'nodeColor',
  'highlightedNodeColor',
  'chartColor',
  'fontColor',
];
export const mapPropColor = (
  props: Record<string, any>,
  appInfo?: AppInfoColor
): any => {
  if (!props) {
    return props;
  }
  if (typeof props !== 'object') {
    return props;
  }
  const newProps: Record<string, any> = {};
  for (const key in props) {
    if (typeof props[key] === 'object' && !Array.isArray(props[key])) {
      newProps[key] = mapPropColor(props[key], appInfo);
      continue;
    }
    if (COLOR_KEYS.includes(key)) {
      newProps[key] = getDynamicColor(props[key], appInfo);
      continue;
    }
    newProps[key] = props[key];
  }
  return newProps;
};

export const updateElementColor = (obj: ElementType, appInfo: AppInfoColor) => {
  if (obj.type === ComponentType.libraryComponent) {
    return {
      ...obj,
      attributes: {
        ...obj?.attributes,
        ...mapPropColor(obj?.attributes, appInfo),
      },
      style: {
        ...obj?.style,
        ...mapPropColor(obj?.style ?? {}, appInfo),
      },
    };
  }

  if (obj.type === ComponentType.pageTemplate) {
    return {
      ...obj,
      breakpointManifest: obj?.breakpointManifest?.map((manifest) => ({
        ...manifest,
        ...mapPropColor(manifest, appInfo),
      })),
      attributes: {
        ...obj?.attributes,
        ...mapPropColor(obj?.attributes, appInfo),
      },
      style: {
        ...obj?.style,
        ...mapPropColor(obj?.style ?? {}, appInfo),
      },
    };
  }

  return {
    ...obj,
    ...mapPropColor(obj, appInfo),
  };
};

const V3_STYLE_KEYS = [
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'width',
  'height',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'textAlign',
  'lineHeight',
  'color',
  'opacity',
  'borderRadius',
  'borderStyle',
  'borderWidth',
  'borderColor',
  'backgroundColor',
  'textColor',
  'disabledTextColor',
  'iconColor',
  'padding',
  'zIndex',
];
export const mapV3StyleToV4Style = (obj: ElementType) => {
  return {
    ...obj,
    style: {
      ...pick(obj, V3_STYLE_KEYS),
      ...obj?.style,
      ...pick(obj.attributes, V3_STYLE_KEYS),
    },
  };
};
